let optimisticSequence = 0;

export const CHAT_DELIVERY_STATE = Object.freeze({
  SENDING: "sending",
  SENT: "sent",
});

export function createChatClientId(now = Date.now()) {
  optimisticSequence += 1;
  return `local-${now}-${optimisticSequence}`;
}

export function buildOptimisticChatMessage({
  clientId = createChatClientId(),
  createdAt = new Date().toISOString(),
  text = "",
  payload = null,
  replyTo = null,
  pendingImages = [],
  authorId = null,
  senderUsername = "@me",
  senderCode = null,
  role = null,
  formatTime = () => "",
} = {}) {
  const attachments = (Array.isArray(pendingImages) ? pendingImages : [])
    .map((image) => {
      const url = String(image?.url || "").trim();
      if (!url) return null;
      return {
        type: "image",
        url,
        original_name: image?.name || image?.file?.name || null,
        size_bytes: Number(image?.size || image?.file?.size || 0) || null,
      };
    })
    .filter(Boolean);

  return {
    type: "message",
    id: clientId,
    client_id: clientId,
    local_echo: true,
    delivery_status: CHAT_DELIVERY_STATE.SENDING,
    created_at: createdAt,
    author_id: authorId,
    message_type: payload?.kind === "sticker" ? "sticker" : "text",
    text,
    payload,
    time: formatTime(createdAt),
    role,
    is_self: true,
    sender_username: senderUsername,
    sender_code: senderCode,
    reply_to_id: replyTo?.id ? String(replyTo.id) : null,
    reply_to: replyTo
      ? { id: replyTo.id, text: replyTo.preview, author_username: replyTo.author }
      : null,
    attachments,
    reactions: [],
    read_by_peer: false,
  };
}

function payloadFingerprint(payload) {
  if (!payload || typeof payload !== "object") return "";
  const kind = String(payload.kind || "").trim().toLowerCase();
  if (kind === "sticker") return `${kind}:${String(payload.sticker_id || "")}`;
  return kind;
}

export function findMatchingOptimisticClientId(items, serverItem) {
  const serverClientId = String(serverItem?.client_id || "").trim();
  if (serverClientId) {
    const exactMatch = (Array.isArray(items) ? items : []).find(
      (item) =>
        item?.delivery_status === CHAT_DELIVERY_STATE.SENDING &&
        String(item?.client_id || "") === serverClientId
    );
    if (exactMatch) return exactMatch.client_id;
  }

  if (!serverItem?.is_self) return null;

  const serverText = String(serverItem?.text || "");
  const serverPayload = payloadFingerprint(serverItem?.payload);
  const serverReplyId = String(serverItem?.reply_to_id || serverItem?.reply_to?.id || "");
  const serverAttachmentCount = Array.isArray(serverItem?.attachments)
    ? serverItem.attachments.length
    : 0;

  const match = (Array.isArray(items) ? items : []).find((item) => {
    if (
      item?.delivery_status !== CHAT_DELIVERY_STATE.SENDING ||
      !item?.is_self ||
      String(item?.text || "") !== serverText ||
      payloadFingerprint(item?.payload) !== serverPayload
    ) {
      return false;
    }

    const pendingReplyId = String(item?.reply_to_id || item?.reply_to?.id || "");
    const pendingAttachmentCount = Array.isArray(item?.attachments)
      ? item.attachments.length
      : 0;
    return (
      pendingReplyId === serverReplyId &&
      pendingAttachmentCount === serverAttachmentCount
    );
  });

  return match?.client_id || null;
}

export function reconcileOptimisticChatMessage(items, clientId, serverItem) {
  const list = Array.isArray(items) ? items : [];
  if (!clientId || !serverItem) return list;

  const serverId = Number(serverItem?.id || 0);
  const optimisticIndex = list.findIndex(
    (item) => String(item?.client_id || "") === String(clientId)
  );
  if (optimisticIndex < 0) return list;

  const optimisticItem = list[optimisticIndex];
  const nextItem = {
    ...optimisticItem,
    ...serverItem,
    client_id: optimisticItem.client_id,
    local_echo: true,
    delivery_status: CHAT_DELIVERY_STATE.SENT,
  };

  const next = [];
  list.forEach((item, index) => {
    if (index === optimisticIndex) {
      next.push(nextItem);
      return;
    }
    if (serverId > 0 && Number(item?.id || 0) === serverId) return;
    next.push(item);
  });
  return next;
}

export function removeOptimisticChatMessage(items, clientId) {
  if (!clientId) return Array.isArray(items) ? items : [];
  return (Array.isArray(items) ? items : []).filter(
    (item) => String(item?.client_id || "") !== String(clientId)
  );
}
